/**
 * Event toasts for a fleet (Sprint 3, S3-14 / D13): while playing, events of all missions become toasts, at most one
 * toast every `gapMs` of real time. Events that arrive in between are collected and merged into the next toast
 * ("3 events — July 1979"), so 1979–1981, with seven giant-planet flybys, stays readable. Pure; times in ms.
 */

export interface ToastEvent {
  /** Event time (UTC ms of mission time). */
  time: number;
  /** Anything that identifies the event (for keys and tests). */
  id: string;
}

export interface Toast<E extends ToastEvent> {
  events: E[];
  /** Real time (ms) the toast was shown. */
  shownAt: number;
}

export const TOAST_GAP_MS = 2000;

export class ToastQueue<E extends ToastEvent> {
  private pending: E[] = [];
  private lastShown = -Infinity;

  constructor(private readonly gapMs = TOAST_GAP_MS) {}

  /** Adds events crossed by playback (in mission-time order). */
  push(events: readonly E[]): void {
    for (const e of events) if (!this.pending.some((p) => p.id === e.id)) this.pending.push(e);
  }

  /** At real time `now`: the next toast if one is due (all pending events merged), else null. */
  poll(now: number): Toast<E> | null {
    if (this.pending.length === 0 || now - this.lastShown < this.gapMs) return null;
    const events = this.pending.sort((a, b) => a.time - b.time);
    this.pending = [];
    this.lastShown = now;
    return { events, shownAt: now };
  }

  /** Forgets pending events (playback stopped or jumped). */
  clear(): void {
    this.pending = [];
  }
}
